import { ReactNode, createContext, useContext, useState } from 'react'
import {
    createCopilotPreset,
    deleteCopilotPreset,
    fetchCopilotPresetsByAuthorId,
    updateCopilotPreset,
    saveCopilotPreset,
    fetchSavedCopilotPresets,
    removedSavedCopilotPreset,
} from 'shared/api/creation'
import UserContext from 'shared/contexts/user'
import { CopilotUploadPreset } from 'shared/types/copilot'
import { PortalMessageContext } from 'ui'
import { MAX_TAG_LIMIT } from '../../../../constants'
import { CreationToolUploadContext } from '../../manager'
import {
    PresetCreatedMessage,
    PresetDeletedMessage,
    PresetDuplicatedMessage,
    PresetImportedMessage,
    PresetRenamedMessage,
    PresetUpdatedMessage,
    PresetRemovedMessage,
} from '../messages'
import { createDataFromPreset } from '../utils/preset'
import { createDataFromUploadContext } from '../utils/upload'
import { useAsyncEffect } from 'utils'

export type CopilotApiContextProps = {
    createdCursor: string
    importedCursor: string
    createNewPreset: (title: string) => Promise<void>
    deletePreset: (preset: CopilotUploadPreset) => Promise<void>
    duplicatePreset: (preset: CopilotUploadPreset, title?: string) => Promise<void>
    importPresetById: (id: string, ignoreMessage?: boolean) => Promise<void>
    renamePreset: (preset: CopilotUploadPreset, newName: string) => Promise<void>
    updatePreset: (preset: CopilotUploadPreset) => Promise<void>
    fetchUserPresets: (cursor?: string) => Promise<void>
    fetchImportedPresets: (cursor?: string) => Promise<void>
    removeImportedPreset: (preset: CopilotUploadPreset) => Promise<void>
}

export const CopilotApiContext = createContext({} as CopilotApiContextProps)

type Props = {
    children: ReactNode
    onCreate?: (preset: CopilotUploadPreset) => void
    onError?: (message: string) => void
    onRemove?: (presetId: string, isImported?: boolean) => void
    onUpdate?: (presets: CopilotUploadPreset[]) => void
    onImportedUpdate?: (presets: CopilotUploadPreset[]) => void
}

const CopilotApiContextManager = ({ children, onCreate, onError, onRemove, onUpdate, onImportedUpdate }: Props) => {
    const { showMessage } = useContext(PortalMessageContext)
    const { user } = useContext(UserContext)
    const uploadContext = useContext(CreationToolUploadContext)
    const [createdCursor, setCreatedCursor] = useState<string>('')
    const [importedCursor, setImportedCursor] = useState<string>('')

    function setIsLocked(preset: CopilotUploadPreset): CopilotUploadPreset {
        const is_locked = preset.author.username !== user.username

        return { ...preset, is_locked }
    }

    async function createNewPreset(title: string) {
        if (!title) return

        if (uploadContext.tags.length > MAX_TAG_LIMIT) {
            showMessage(`Presets have a limit of ${MAX_TAG_LIMIT} tags. Please remove some tags and try again.`, {
                style: 'error',
            })

            return
        }

        try {
            const data = createDataFromUploadContext(title, uploadContext)
            const created = await createCopilotPreset(data).then(setIsLocked)

            showMessage(<PresetCreatedMessage presetName={created.name} />)
            onCreate?.(created)
        } catch (err) {
            onError?.(`An error occurred while creating your preset.`)
        }
    }

    async function removeImportedPreset(preset: CopilotUploadPreset) {
        try {
            await removedSavedCopilotPreset(preset.id)

            showMessage(<PresetRemovedMessage presetName={preset.name} />)
            onRemove?.(preset.id, true)
        } catch (err) {
            onError?.(`An error occurred while removing preset.`)
        }
    }

    async function deletePreset(preset: CopilotUploadPreset) {
        try {
            await deleteCopilotPreset(preset.id)

            showMessage(<PresetDeletedMessage presetName={preset.name} />)
            onRemove?.(preset.id)
        } catch (err) {
            onError?.(`An error occurred while deleting preset.`)
        }
    }

    async function duplicatePreset(preset: CopilotUploadPreset, title?: string) {
        try {
            const name = title || `${preset.name} copy`
            const data = createDataFromPreset(name, preset)
            const duplicated = await createCopilotPreset(data).then(setIsLocked)

            showMessage(<PresetDuplicatedMessage presetName={preset.name} />)
            onUpdate?.([duplicated])
        } catch (err) {
            onError?.(`An error occurred while duplicating preset.`)
        }
    }

    async function importPresetById(id: string, ignoreMessage: boolean = false) {
        try {
            const imported = await saveCopilotPreset(id).then(setIsLocked)
            if (!ignoreMessage) {
                showMessage(<PresetImportedMessage presetName={imported.name} />)
            }
            onImportedUpdate?.([imported])
        } catch (err) {
            console.error(err)
            onError?.(`An error occurred while importing preset.`)
        }
    }

    async function renamePreset(preset: CopilotUploadPreset, newName: string) {
        if (!newName || newName === preset.name) {
            return
        }

        try {
            const data = createDataFromPreset(newName, preset)
            const renamed = await updateCopilotPreset(preset.id, {
                ...data,
                update_datetime: preset.update_datetime,
            })

            showMessage(<PresetRenamedMessage oldPresetName={preset.name} presetName={newName} />)
            onUpdate?.([renamed])
        } catch (err) {
            onError?.('An error occurred while renaming preset.')
        }
    }

    async function updatePreset(preset: CopilotUploadPreset) {
        try {
            const data = createDataFromUploadContext(preset.name, uploadContext, preset.upload_optional_tags.tags)
            const updated = await updateCopilotPreset(preset.id, {
                ...data,
                update_datetime: preset.update_datetime,
            }).then(setIsLocked)

            showMessage(<PresetUpdatedMessage presetName={updated.name} />)
            onUpdate?.([updated])
        } catch (err) {
            onError?.(`An error occurred while updating preset.`)
        }
    }

    // create a function to import presets that the user has created
    async function fetchUserPresets(cursor?: string) {
        try {
            const data = await fetchCopilotPresetsByAuthorId(`${user.id}`, cursor)
            setCreatedCursor(data.cursor)
            onUpdate?.(data.upload_presets)
        } catch (err) {
            console.error('An error occurred while fetching your presets.')
        }
    }

    // create a function to import presets that the user has imported

    async function fetchImportedPresets(cursor?: string) {
        try {
            const data = await fetchSavedCopilotPresets(cursor)
            const mappedPresets = data.upload_presets.map(setIsLocked)
            // console.log('Fetch saved copilot presets data: ', data)
            setImportedCursor(data.cursor)
            onImportedUpdate?.(mappedPresets)
        } catch (err) {
            console.error('An error occurred fetching your saved presets')
        }
    }

    useAsyncEffect(async () => {
        await fetchImportedPresets()
        await fetchUserPresets()
    }, [])

    return (
        <CopilotApiContext.Provider
            value={{
                createdCursor,
                importedCursor,
                createNewPreset,
                deletePreset,
                duplicatePreset,
                importPresetById,
                renamePreset,
                updatePreset,
                fetchUserPresets,
                fetchImportedPresets,
                removeImportedPreset,
            }}
        >
            {children}
        </CopilotApiContext.Provider>
    )
}

export default CopilotApiContextManager
